import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { 
  Typography, 
  Button, 
  Input, 
  Select, 
  InputNumber, 
  Row, 
  Col, 
  Card, 
  Divider, 
  Alert,
  Spin,
  Steps
} from 'antd';
import { 
  UserOutlined, 
  MailOutlined, 
  PhoneOutlined, 
  EnvironmentOutlined,
  InstagramOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import { submitKolApplication } from '../services/kolService';

const { Title, Paragraph } = Typography;
const { Option } = Select;
const { Step } = Steps;

const FormContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const StyledCard = styled(Card)`
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  margin-bottom: 24px;
`;

const ErrorText = styled.div`
  color: #ff4d4f;
  font-size: 14px;
  margin-top: 4px;
`;

const SuccessCard = styled(Card)`
  text-align: center;
  padding: 40px;
  background-color: #f6ffed;
  border-color: #b7eb8f;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-radius: 8px;
`;

const SuccessIcon = styled(CheckCircleOutlined)`
  font-size: 72px;
  color: #52c41a;
  margin-bottom: 24px;
`;

// Sample category options - in a real app, these could be fetched from an API
const categoryOptions = [
  'Beauty', 
  'Fashion', 
  'Lifestyle', 
  'Fitness', 
  'Technology', 
  'Food', 
  'Travel', 
  'Entertainment',
  'Gaming',
  'Business'
];

const KolSubmissionSchema = Yup.object().shape({
  name: Yup.string()
    .required('Name is required')
    .min(2, 'Name must be at least 2 characters'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  phone: Yup.string()
    .required('Phone number is required'),
  location: Yup.string()
    .required('Location is required'),
  instagramHandle: Yup.string()
    .required('Instagram handle is required')
    .matches(/^[a-zA-Z0-9._]+$/, 'Invalid Instagram handle format'),
  instagramFollowers: Yup.number()
    .required('Instagram followers count is required')
    .min(1000, 'Minimum 1,000 followers required'),
  tiktokHandle: Yup.string()
    .matches(/^[a-zA-Z0-9._]*$/, 'Invalid TikTok handle format'),
  tiktokFollowers: Yup.number()
    .when(['tiktokHandle'], {
      is: (tiktokHandle: string) => tiktokHandle && tiktokHandle.length > 0,
      then: (schema) => schema.required('TikTok followers count is required'),
      otherwise: (schema) => schema
    }),
  categories: Yup.array()
    .of(Yup.string())
    .min(1, 'At least one category is required')
    .required('At least one category is required'),
});

const KolSubmissionForm: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  
  const handleSubmit = async (values: any) => {
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      await submitKolApplication(values);
      setSubmitSuccess(true);
      setCurrentStep(2);
    } catch (error: any) {
      setSubmitError(error.message || 'Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const steps = [
    {
      title: 'Personal Info',
      content: (props: any) => (
        <>
          <Row gutter={16}>
            <Col span={12}>
              <Field name="name">
                {({ field, meta }: any) => (
                  <div style={{ marginBottom: 24 }}>
                    <Typography.Text strong>Name *</Typography.Text>
                    <Input
                      {...field}
                      placeholder="Your full name"
                      prefix={<UserOutlined />}
                      status={meta.touched && meta.error ? 'error' : ''}
                      size="large"
                    />
                    <ErrorMessage name="name" component={ErrorText} />
                  </div>
                )}
              </Field>
            </Col>
            <Col span={12}>
              <Field name="email">
                {({ field, meta }: any) => (
                  <div style={{ marginBottom: 24 }}>
                    <Typography.Text strong>Email *</Typography.Text>
                    <Input
                      {...field}
                      placeholder="Your email address"
                      prefix={<MailOutlined />}
                      status={meta.touched && meta.error ? 'error' : ''}
                      size="large"
                    />
                    <ErrorMessage name="email" component={ErrorText} />
                  </div>
                )}
              </Field>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Field name="phone">
                {({ field, meta }: any) => (
                  <div style={{ marginBottom: 24 }}>
                    <Typography.Text strong>Phone Number *</Typography.Text>
                    <Input
                      {...field}
                      placeholder="Your phone number"
                      prefix={<PhoneOutlined />}
                      status={meta.touched && meta.error ? 'error' : ''}
                      size="large"
                    />
                    <ErrorMessage name="phone" component={ErrorText} />
                  </div>
                )}
              </Field>
            </Col>
            <Col span={12}>
              <Field name="location">
                {({ field, meta }: any) => (
                  <div style={{ marginBottom: 24 }}>
                    <Typography.Text strong>Location *</Typography.Text>
                    <Input
                      {...field}
                      placeholder="Your city/country"
                      prefix={<EnvironmentOutlined />}
                      status={meta.touched && meta.error ? 'error' : ''}
                      size="large"
                    />
                    <ErrorMessage name="location" component={ErrorText} />
                  </div>
                )}
              </Field>
            </Col>
          </Row>
          
          <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              type="primary" 
              size="large"
              onClick={() => {
                if (
                  props.values.name && 
                  props.values.email && 
                  props.values.phone && 
                  props.values.location &&
                  !props.errors.name && 
                  !props.errors.email && 
                  !props.errors.phone && 
                  !props.errors.location
                ) {
                  setCurrentStep(1);
                } else {
                  props.setTouched({
                    name: true,
                    email: true,
                    phone: true,
                    location: true
                  });
                }
              }}
            >
              Next
            </Button>
          </div>
        </>
      )
    },
    {
      title: 'Social Media',
      content: (props: any) => (
        <>
          <Row gutter={16}>
            <Col span={12}>
              <Field name="instagramHandle">
                {({ field, meta }: any) => (
                  <div style={{ marginBottom: 24 }}>
                    <Typography.Text strong>Instagram Handle *</Typography.Text>
                    <Input
                      {...field}
                      placeholder="Your Instagram handle (without @)"
                      prefix={<InstagramOutlined style={{ color: '#E1306C' }} />}
                      status={meta.touched && meta.error ? 'error' : ''}
                      size="large"
                    />
                    <ErrorMessage name="instagramHandle" component={ErrorText} />
                  </div>
                )}
              </Field>
            </Col>
            <Col span={12}>
              <Field name="instagramFollowers">
                {({ field, meta, form }: any) => (
                  <div style={{ marginBottom: 24 }}>
                    <Typography.Text strong>Instagram Followers *</Typography.Text>
                    <InputNumber
                      value={field.value}
                      onChange={(value) => form.setFieldValue('instagramFollowers', value)}
                      placeholder="Number of followers"
                      style={{ width: '100%' }}
                      min={0}
                      formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
                      status={meta.touched && meta.error ? 'error' : ''}
                      size="large"
                    />
                    <ErrorMessage name="instagramFollowers" component={ErrorText} />
                  </div>
                )}
              </Field>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Field name="tiktokHandle">
                {({ field, meta }: any) => (
                  <div style={{ marginBottom: 24 }}>
                    <Typography.Text strong>TikTok Handle (Optional)</Typography.Text>
                    <Input
                      {...field}
                      placeholder="Your TikTok handle (without @)"
                      status={meta.touched && meta.error ? 'error' : ''}
                      size="large"
                    />
                    <ErrorMessage name="tiktokHandle" component={ErrorText} />
                  </div>
                )}
              </Field>
            </Col>
            <Col span={12}>
              <Field name="tiktokFollowers">
                {({ field, meta, form }: any) => (
                  <div style={{ marginBottom: 24 }}>
                    <Typography.Text strong>TikTok Followers</Typography.Text>
                    <InputNumber
                      value={field.value}
                      onChange={(value) => form.setFieldValue('tiktokFollowers', value)}
                      placeholder="Number of followers"
                      style={{ width: '100%' }}
                      min={0}
                      formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
                      status={meta.touched && meta.error ? 'error' : ''}
                      size="large"
                    />
                    <ErrorMessage name="tiktokFollowers" component={ErrorText} />
                  </div>
                )}
              </Field>
            </Col>
          </Row>
          
          <Field name="categories">
            {({ field, meta, form }: any) => (
              <div style={{ marginBottom: 24 }}>
                <Typography.Text strong>Content Categories *</Typography.Text>
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  placeholder="Select categories that match your content"
                  value={field.value}
                  onChange={(value) => form.setFieldValue('categories', value)}
                  status={meta.touched && meta.error ? 'error' : ''}
                  size="large"
                >
                  {categoryOptions.map(category => (
                    <Option key={category} value={category}>{category}</Option>
                  ))}
                </Select>
                <ErrorMessage name="categories" component={ErrorText} />
              </div>
            )}
          </Field>
          
          <Field name="notes">
            {({ field }: any) => (
              <div style={{ marginBottom: 24 }}>
                <Typography.Text strong>Additional Notes (Optional)</Typography.Text>
                <Input.TextArea
                  {...field}
                  placeholder="Tell us more about yourself and your content"
                  rows={4}
                  size="large"
                />
              </div>
            )}
          </Field>
          
          <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between' }}>
            <Button 
              size="large"
              onClick={() => setCurrentStep(0)}
            >
              Previous
            </Button>
            <Button 
              type="primary" 
              size="large"
              htmlType="submit"
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              Submit Application
            </Button>
          </div>
        </>
      )
    },
    {
      title: 'Completed',
      content: () => (
        <SuccessCard>
          <SuccessIcon />
          <Title level={3}>Application Submitted Successfully!</Title>
          <Paragraph>
            Thank you for your interest in becoming a Key Opinion Leader. We have received your application and will review it shortly.
          </Paragraph>
          <Paragraph>
            Someone from our team will contact you within 5-7 business days regarding the next steps.
          </Paragraph>
          <Button 
            type="primary" 
            size="large"
            href="/"
            style={{ marginTop: 24 }}
          >
            Return to Home
          </Button>
        </SuccessCard>
      )
    }
  ];
  
  return (
    <FormContainer>
      <StyledCard>
        <Title level={2} style={{ textAlign: 'center' }}>
          KOL Application
        </Title>
        <Paragraph style={{ textAlign: 'center', fontSize: 16 }}>
          Join our network of Key Opinion Leaders and collaborate with top brands
        </Paragraph>
        
        <Divider />
        
        <Steps current={currentStep} style={{ marginBottom: 40 }}>
          {steps.map(step => (
            <Step key={step.title} title={step.title} />
          ))}
        </Steps>
        
        {submitError && (
          <Alert
            message="Submission Error"
            description={submitError}
            type="error"
            showIcon
            style={{ marginBottom: 24 }}
          />
        )}
        
        <Spin spinning={isSubmitting}>
          {submitSuccess ? (
            steps[2].content({})
          ) : (
            <Formik
              initialValues={{
                name: '',
                email: '',
                phone: '',
                location: '',
                instagramHandle: '',
                instagramFollowers: null,
                tiktokHandle: '',
                tiktokFollowers: null,
                categories: [],
                notes: '',
              }}
              validationSchema={KolSubmissionSchema}
              onSubmit={handleSubmit}
            >
              {(formikProps) => (
                <Form>
                  {steps[currentStep].content(formikProps)}
                </Form>
              )}
            </Formik>
          )}
        </Spin>
      </StyledCard>
    </FormContainer>
  );
};

export default KolSubmissionForm; 